import type {
  ItemCondition,
  OwnershipStatus,
  TradeStatus,
  Visibility,
} from '@garagegymbros/contracts';
import { usernameSchema } from '@garagegymbros/contracts';
export * from './seo';

/** Existing server-generated provisional names remain addressable until the member chooses one. */
export function normalizeProfileName(value: string): string | null {
  const parsed = usernameSchema.safeParse(value);
  if (parsed.success) return parsed.data;
  const name = value.trim().toLowerCase();
  return /^member_[a-f0-9]{32}$/.test(name) ? name : null;
}
export function profilePath(username: string): string {
  const name = normalizeProfileName(username);
  if (!name) throw new Error('Invalid profile name.');
  return `/${name}`;
}
export function profileUrl(username: string): string {
  return `https://garagegymbros.com${profilePath(username)}`;
}
export function memberProfilePath(member: { id: string; username?: string }): string {
  return member.username && normalizeProfileName(member.username)
    ? profilePath(member.username)
    : `/profile/${encodeURIComponent(member.id)}`;
}
export function profileNameFromPath(pathname: string): string | null {
  if (!/^\/[^/]+\/?$/.test(pathname)) return null;
  try {
    return normalizeProfileName(decodeURIComponent(pathname.replace(/^\/|\/$/g, '')));
  } catch {
    return null;
  }
}
export const APP_LIMITS = Object.freeze({
  page_size: 24,
  max_page_size: 100,
  story_lifetime_ms: 86400000,
  max_post_characters: 5000,
  max_message_characters: 5000,
  media_enabled: false,
  optional_email_enabled: false,
  minimum_age: 18,
});
const visibilityRank: Record<Visibility, number> = { private: 0, followers: 1, public: 2 };
/** A child can narrow but cannot broaden its parent's audience. DB RLS remains authoritative. */
export function effectiveVisibility(parent: Visibility, child: Visibility): Visibility {
  return visibilityRank[parent] < visibilityRank[child] ? parent : child;
}
export function canView(input: {
  visibility: Visibility;
  isOwner: boolean;
  acceptedFollower: boolean;
  blocked: boolean;
  active: boolean;
}): boolean {
  if (input.isOwner) return true;
  if (input.blocked || !input.active) return false;
  return (
    input.visibility === 'public' || (input.visibility === 'followers' && input.acceptedFollower)
  );
}
const tradeTransitions: Record<TradeStatus, readonly TradeStatus[]> = {
  available: ['reserved', 'withdrawn'],
  reserved: ['available', 'completed', 'withdrawn'],
  completed: [],
  withdrawn: [],
  hidden: [],
};
export function canTransitionTrade(from: TradeStatus, to: TradeStatus): boolean {
  return tradeTransitions[from].includes(to);
}
export function assertTradeTransition(from: TradeStatus, to: TradeStatus): void {
  if (!canTransitionTrade(from, to)) throw new Error(`Cannot change ${from} to ${to}.`);
}
export function canListItem(status: OwnershipStatus, condition: ItemCondition): boolean {
  return status === 'owned' && condition !== undefined;
}
export function isStoryActive(expiresAt: string, now = new Date()): boolean {
  return Number.isFinite(Date.parse(expiresAt)) && Date.parse(expiresAt) > now.getTime();
}
export function summarizeRatings(ratings: readonly number[]): {
  count: number;
  average: number | null;
  distribution: Record<number, number>;
} {
  const distribution: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  for (const rating of ratings) {
    if (!Number.isFinite(rating) || !Number.isInteger(rating * 2) || rating < 0.5 || rating > 5)
      throw new Error('Ratings must be half-star steps between 0.5 and 5.');
    distribution[Math.ceil(rating)] = (distribution[Math.ceil(rating)] ?? 0) + 1;
  }
  return {
    count: ratings.length,
    average: ratings.length ? ratings.reduce((a, b) => a + b, 0) / ratings.length : null,
    distribution,
  };
}
export function isOfferFresh(observedAt: string, now = new Date(), maxAgeDays = 7): boolean {
  const time = Date.parse(observedAt);
  return (
    Number.isFinite(time) && time <= now.getTime() && now.getTime() - time <= maxAgeDays * 86400000
  );
}
export function formatMoney(amount: number | null, currency = 'USD', locale = 'en-US'): string {
  return amount === null
    ? 'Price unknown'
    : new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
}
export function safeReturnTo(value: string | null | undefined, fallback = '/home'): string {
  if (
    !value ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\') ||
    /[\r\n]/.test(value)
  )
    return fallback;
  try {
    const url = new URL(value, 'https://garagegymbros.com');
    return url.origin === 'https://garagegymbros.com'
      ? url.pathname + url.search + url.hash
      : fallback;
  } catch {
    return fallback;
  }
}
export interface Cursor {
  offset: number;
  version: 1;
}
/** Bounded opaque offset cursor; pagination is always ordered in repository queries. */
export function encodeCursor(offset: number): string {
  if (!Number.isInteger(offset) || offset < 0 || offset > 100000)
    throw new Error('Invalid page offset.');
  return `v1.${offset.toString(36)}`;
}
export function decodeCursor(cursor?: string | null): number {
  if (!cursor) return 0;
  if (!/^v1\.[0-9a-z]{1,4}$/.test(cursor)) throw new Error('Invalid pagination cursor.');
  const offset = parseInt(cursor.slice(3), 36);
  if (offset > 100000) throw new Error('Pagination limit exceeded.');
  return offset;
}
export function nextCursor(offset: number, returned: number, limit: number): string | null {
  return returned === limit && offset + returned <= 100000 ? encodeCursor(offset + returned) : null;
}
export function escapeSearchTerm(value: string): string {
  return value
    .replace(/[\\%_,().]/g, ' ')
    .trim()
    .slice(0, 100);
}

export { publicVideoEmbed, type VideoEmbed } from './videoEmbed.js';
